import React, { useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Rate, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Alert,
  Upload,
  message
} from 'antd';
import { 
  StarOutlined, 
  CameraOutlined, 
  CheckCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const ReviewCollection = ({ visible, onClose, serviceDetails }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setSubmitting(false);
    setSubmitted(true);
    
    // Show success message
    message.success('Thank you for your review! It will be published within 24 hours.');
    
    // Close modal after 3 seconds
    setTimeout(() => {
      onClose();
      setSubmitted(false);
      form.resetFields();
    }, 3000);
  };

  const reviewTemplates = [
    "The team was professional and did excellent work!",
    "Great service, highly recommend!",
    "Very satisfied with the quality and timeliness.",
    "The crew was friendly and cleaned up perfectly.",
    "Excellent value for the money, will use again!",
    "Professional service from start to finish.",
    "Exceeded my expectations, thank you!",
    "Quick response and great results."
  ];

  if (submitted) {
    return (
      <Modal
        title="Review Submitted Successfully!"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={500}
        centered
      >
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <CheckCircleOutlined style={{ fontSize: '4rem', color: '#52c41a', marginBottom: '1rem' }} />
          <Title level={3} style={{ color: '#52c41a' }}>Thank You!</Title>
          <Paragraph>
            Your review has been submitted and will be published within 24 hours.
            <br />
            We appreciate your feedback and continued trust in our services.
          </Paragraph>
          <Text type="secondary">
            This window will close automatically...
          </Text>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StarOutlined style={{ marginRight: 8, color: '#faad14' }} />
          Share Your Experience
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <div style={{ marginBottom: '1.5rem' }}>
        <Alert
          message="Help others discover our services!"
          description="Your review helps potential customers make informed decisions and helps us improve our services."
          type="info"
          showIcon
          style={{ marginBottom: '1rem' }}
        />
        
        {serviceDetails && (
          <Card size="small" style={{ marginBottom: '1rem' }}>
            <Text strong>Service Completed:</Text>
            <br />
            <Text>{serviceDetails.service} - {serviceDetails.date}</Text>
          </Card>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ rating: 5 }}
      >
        <Form.Item
          name="rating"
          label="Overall Rating"
          rules={[{ required: true, message: 'Please provide a rating' }]}
        >
          <Rate 
            style={{ fontSize: '2rem' }} 
            allowClear={false}
            tooltips={['Poor', 'Fair', 'Good', 'Very Good', 'Excellent']}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label="Review Title"
          rules={[{ required: true, message: 'Please provide a title' }]}
        >
          <Input 
            placeholder="Summarize your experience in a few words"
            maxLength={100}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="review"
          label="Your Review"
          rules={[
            { required: true, message: 'Please write a review' },
            { min: 10, message: 'Please write at least 10 characters' }
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Tell others about your experience with our service..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="photos"
          label="Photos (Optional)"
        >
          <Upload
            listType="picture-card"
            maxCount={3}
            beforeUpload={() => false} // Prevent auto upload
            accept="image/*"
          >
            <div>
              <CameraOutlined />
              <div style={{ marginTop: 8 }}>Upload Photos</div>
            </div>
          </Upload>
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            Share before/after photos or work results (max 3 photos)
          </Text>
        </Form.Item>

        <Form.Item
          name="recommend"
          label="Would you recommend us to others?"
          rules={[{ required: true, message: 'Please select an option' }]}
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="recommend" value="yes" style={{ marginRight: '0.5rem' }} />
              <Text>Yes, definitely!</Text>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="recommend" value="maybe" style={{ marginRight: '0.5rem' }} />
              <Text>Maybe</Text>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="recommend" value="no" style={{ marginRight: '0.5rem' }} />
              <Text>No</Text>
            </label>
          </div>
        </Form.Item>

        <div style={{ marginBottom: '1rem' }}>
          <Text strong>Quick Review Templates:</Text>
          <div style={{ marginTop: '0.5rem' }}>
            {reviewTemplates.map((template, index) => (
              <Button
                key={index}
                size="small"
                type="dashed"
                style={{ margin: '2px', fontSize: '0.8rem' }}
                onClick={() => form.setFieldsValue({ review: template })}
              >
                {template}
              </Button>
            ))}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Space>
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={submitting}
              size="large"
              style={{ 
                background: 'linear-gradient(135deg, #1890ff, #40a9ff)',
                border: 'none'
              }}
            >
              Submit Review
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default ReviewCollection;
